import {
  Alert,
  Box,
  Columns,
  Stack,
  Text,
  ImageWithFallback,
  Card,
  LinkNext,
} from '@instamenu/ui-system-next';
import { CheckoutRestaurantFieldsFragment } from '@instamenu/context-checkout';
import { formatAddress, formatPhone } from '@instamenu/formatters';
import * as style from './restaurant-ledespace.css';
import { themeVars } from '@instamenu/ui-vanilla-extract';
import { DeliveryPickupToggle } from '../delivery-pickup-toggle/delivery-pickup-toggle';
import { OpeningHours } from '../opening-hours/opening-hours';
import { EstimatedWait } from '../estimated-wait/estimated-wait';
import { MapLink } from '@instamenu/ui-system-next';
import { Phone, MapPin } from 'lucide-react';

export async function RestaurantLedespace({
  restaurant,
  menuId,
  menuName,
}: {
  restaurant: CheckoutRestaurantFieldsFragment;
  menuId?: string;
  menuName?: string | null;
}) {
  if (!restaurant) return null;

  const hasInfoContent =
    restaurant.tagline || restaurant.address || restaurant.phone || restaurant.setting.openingHours;

  return (
    <section style={{ position: 'relative' }}>
      {restaurant.heroImageUrl && (
        <Columns>
          <div style={{ height: 180, position: 'relative', width: '100%' }}>
            <ImageWithFallback
              src={restaurant.heroImageUrl}
              alt={restaurant.name ?? ''}
              width={1000}
              height={220}
              priority
              style={{
                width: '100%',
              }}
              className={style.banner}
            />
          </div>
        </Columns>
      )}
      <Columns style={{ padding: themeVars.pad[12] }}>
        {restaurant.logoImageUrl && (
          <ImageWithFallback
            src={restaurant.logoImageUrl}
            alt={restaurant.name ?? ''}
            width={80}
            height={80}
            priority
            className={style.logo}
          />
        )}
        <Box className={style.infoContainer}>
          <Stack style={{ gap: 8, width: '100%' }}>
            <Text variant="h1" css={{ marginTop: 52 }}>
              {restaurant.name}
              {menuName ? ` - ${menuName}` : ''}
            </Text>
            <Box className={style.flex}>
              {hasInfoContent && (
                <Card className={style.infoCard}>
                  {restaurant.tagline && (
                    <Text wordBreak variant="caption1">
                      {restaurant.tagline}
                    </Text>
                  )}
                  <Stack css={{ gap: 8 }}>
                    {restaurant.address && (
                      <MapLink
                        css={{ display: 'flex', alignItems: 'center', gap: 4 }}
                        mappable={restaurant}
                      >
                        <MapPin color={themeVars.color.brand} size={16} />
                        <Text variant="caption1">
                          {formatAddress(restaurant.address, { short: true })}
                        </Text>
                      </MapLink>
                    )}
                    {restaurant.phone && (
                      <LinkNext
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                        href={`tel:${restaurant.phone}`}
                      >
                        <Phone size={16} />
                        <Text variant="caption1">{formatPhone(restaurant.phone)}</Text>
                      </LinkNext>
                    )}
                    <OpeningHours restaurant={restaurant} menuId={menuId} />
                  </Stack>
                </Card>
              )}
              <Box className={style.deliveryPickupToggleContainer}>
                <DeliveryPickupToggle deliveryEnabled={restaurant.setting.enableDelivery} />
                <EstimatedWait />
              </Box>
            </Box>
          </Stack>
        </Box>
        {restaurant.setting.storefront.banner && (
          <Alert
            className={style.alert}
            message={restaurant.setting.storefront.banner}
            severity="error"
          />
        )}
      </Columns>
    </section>
  );
}

export default RestaurantLedespace;
